import React, { useState, useEffect, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Loader2, User2 } from "lucide-react";
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { User, UserRole } from '@/types';
import { UserRole as UserRoleEnum } from '@/types/enums';
import { useDebounce } from '@/hooks/useDebounce';
import { EmployeeStatus, ResidencyType, WorkerType } from '@/contexts/employees/EmployeesContext';
import { useAuth } from '@/contexts/auth';

interface SearchEmployeeInputProps {
  value: string;
  onChange: (employee: User | null) => void;
  userRole?: UserRole;
  userDepartment?: string[];
  disabled?: boolean;
  hideLabel?: boolean;
}

export function SearchEmployeeInput({
  value,
  onChange,
  userRole,
  userDepartment,
  disabled = false,
  hideLabel = false
}: SearchEmployeeInputProps) {
  const { user } = useAuth();
  const [search, setSearch] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<User | null>(null);
  const [justSelected, setJustSelected] = useState(false);
  const debouncedSearch = useDebounce(search, 300);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Check if search meets criteria: 3+ letters OR 2+ numbers
  const shouldSearch = (searchTerm: string): boolean => {
    if (!searchTerm || searchTerm.length < 2) return false;
    
    // If it's mostly numbers (employee ID), require 2+ characters
    if (/^\d+$/.test(searchTerm)) return searchTerm.length >= 2;
    
    // If it's letters (name/email), require 3+ characters
    return searchTerm.length >= 3;
  };

  // Search employees when criteria met
  const { data: employees, isLoading } = useQuery<User[]>({
    queryKey: ['employee-search-simple', debouncedSearch, userRole, userDepartment, user?.companyId, EmployeeStatus.ACTIVE],
    queryFn: async (): Promise<User[]> => {
      let query = supabase
        .from('employees')
        .select('id, first_name, last_name, email, department, employee_id, position, company_id, status, shift, residency, worker_type')
        .limit(15); // Keep it small for performance

      // Company filtering - enforce for all roles
      if (user?.companyId) {
        query = query.eq('company_id', user.companyId);
      }

      // Status filtering - only show active employees
      query = query.eq('status', EmployeeStatus.ACTIVE);

      // Department filtering
      if ((userRole === UserRoleEnum.DEPARTMENT_HEAD || userRole === UserRoleEnum.SUPERINTENDENT || userRole === UserRoleEnum.PLANT_MANAGER) && userDepartment?.length) {
        query = query.in('department', userDepartment);
      }

      // Smart search - different strategies for numbers vs letters
      const searchTerm = debouncedSearch.toLowerCase();
      if (/^\d+$/.test(searchTerm)) {
        // Number search - employee ID
        query = query.ilike('employee_id', `%${searchTerm}%`);
      } else {
        // Text search - name or email
        query = query.or(
          `first_name.ilike.%${searchTerm}%,last_name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%`
        );
      }

      const { data, error } = await query;
      if (error) throw error;

      return (data || []).map(emp => ({
        id: emp.id,
        email: emp.email || '',
        role: UserRoleEnum.EMPLOYEE as UserRole,
        firstName: emp.first_name || '',
        lastName: emp.last_name || '',
        name: `${emp.first_name || ''} ${emp.last_name || ''}`.trim(),
        department: emp.department || '',
        position: emp.position || '',
        companyId: emp.company_id || '',
        status: emp.status || EmployeeStatus.ACTIVE,
        shift: emp.shift || '',
        residency: emp.residency || ResidencyType.RESIDENT,
        workerType: emp.worker_type || WorkerType.FULL_TIME,
        employee_id: emp.employee_id || ''
      }));
    },
    enabled: shouldSearch(debouncedSearch),
    staleTime: 0, // No cache to ensure fresh data
    gcTime: 0 // Don't cache results (renamed from cacheTime in newer React Query)
  });

  // Show dropdown only when focused and we have a searchable query, and not immediately after a selection
  useEffect(() => {
    if (!isFocused) {
      setShowDropdown(false);
      return;
    }

    if (justSelected) {
      setShowDropdown(false);
      return;
    }

    if (shouldSearch(debouncedSearch)) {
      setShowDropdown(true);
    } else {
      setShowDropdown(false);
    }
  }, [debouncedSearch, justSelected, isFocused]);

  // Handle outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
        setJustSelected(false);
        setIsFocused(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Only reset when value is explicitly cleared from outside
  useEffect(() => {
    if (!value && selectedEmployee) {
      setSelectedEmployee(null);
      setSearch("");
      setJustSelected(false);
    }
  }, [value, selectedEmployee]);

  const handleSelect = (employee: User) => {
    setSelectedEmployee(employee);
    setSearch(`${employee.employee_id} - ${employee.name}`);
    setShowDropdown(false);
    setJustSelected(true);
    onChange(employee);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setSearch(newValue);
    
    // Reset the justSelected flag when user starts typing again
    if (justSelected) {
      setJustSelected(false);
    }
    
    // Clear selection if user is typing something completely different
    if (selectedEmployee && newValue && !newValue.includes(selectedEmployee.employee_id) && !newValue.includes(selectedEmployee.name)) {
      setSelectedEmployee(null);
      onChange(null);
    }
  };

  return (
    <div className={hideLabel ? "" : "space-y-2"}>
      {!hideLabel && <label className="block text-sm font-medium">Employee</label>}
      
      <div className="relative" ref={dropdownRef}>
        <Input
          ref={inputRef}
          value={search}
          onChange={handleInputChange}
          onFocus={() => {
            setIsFocused(true);
            if (!justSelected && shouldSearch(search)) {
              setShowDropdown(true);
            }
          }}
          onBlur={() => {
            // Defer to allow dropdown clicks (onMouseDown stops blur inside dropdown)
            setTimeout(() => {
              if (!dropdownRef.current?.contains(document.activeElement as Node)) {
                setIsFocused(false);
                setShowDropdown(false);
              }
            }, 0);
          }}
          placeholder="Type 3+ letters (name) or 2+ numbers (ID)..."
          disabled={disabled}
          className="h-11"
        />

        {/* Dropdown */}
        {showDropdown && (
          <div 
            className="absolute z-50 w-full mt-1 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-md shadow-lg max-h-[300px] overflow-y-auto"
            onMouseDown={(e) => e.preventDefault()} // Prevent input blur when clicking in dropdown
          >
            
            {/* Loading */}
            {isLoading && (
              <div className="flex items-center gap-2 px-3 py-3 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Searching...
              </div>
            )}

            {/* Results */}
            {!isLoading && employees && employees.length > 0 && (
              <>
                <div className="px-3 py-2 text-xs text-muted-foreground border-b border-slate-100 dark:border-slate-700">
                  {employees.length} employee{employees.length === 1 ? '' : 's'} found
                </div>
                {employees.map((employee) => (
                  <div
                    key={employee.id}
                    className="flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 dark:hover:bg-slate-700/50 cursor-pointer border-b border-slate-50 dark:border-slate-700/50 last:border-0"
                    onClick={() => handleSelect(employee)}
                  >
                    <Badge variant="outline" className="text-xs font-mono shrink-0 min-w-[50px]">
                      {employee.employee_id || 'N/A'}
                    </Badge>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm truncate">{employee.name}</div>
                      <div className="text-xs text-muted-foreground truncate">
                        {employee.department} • {employee.email}
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}

            {/* No results */}
            {!isLoading && employees && employees.length === 0 && (
              <div className="flex flex-col items-center gap-2 px-3 py-6 text-center">
                <User2 className="h-6 w-6 text-muted-foreground" />
                <div className="text-sm text-muted-foreground">No employees found</div>
                <div className="text-xs text-muted-foreground">
                  Try different search terms
                </div>
              </div>
            )}
          </div>
        )}
      </div>


    </div>
  );
}
